import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, ToggleLeft, ToggleRight, Upload } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { Button } from "../../components/ui/button.js";
import { Input } from "../../components/ui/input.js";
import { Badge } from "../../components/ui/badge.js";
import { Checkbox } from "../../components/ui/checkbox.js";
import { Pagination } from "../../components/ui/pagination.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { useConfirm } from "../../components/ui/confirm-dialog.js";
import { DropdownSelect } from "../../components/DropdownButton.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table.js";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs.js";

import { MultiSelect } from "../../components/ui/multi-select.js";
import { ASSIGNABLE_ROLES } from "@hrms/types";
import { DeleteIconButton, EditIconButton, EmptyState, Field, usePaged } from "./shared.js";
import SkillImportDialog from "./SkillImportDialog.js";
import { skillDisplayName, type ExpertiseLevel, type SkillRow, type SkillType } from "./types.js";

// Skill types are browsed in this order everywhere in the catalog — Technical,
// then Soft, then Functional — rather than alphabetically; any other type
// (added later) sorts after these three, alphabetically among themselves.
const SKILL_TYPE_ORDER = ["Technical Skills", "Soft Skills", "Functional Skills"];
function skillTypeRank(name: string): number {
  const i = SKILL_TYPE_ORDER.indexOf(name);
  return i === -1 ? SKILL_TYPE_ORDER.length : i;
}
function compareSkillTypeName(a: string, b: string): number {
  return skillTypeRank(a) - skillTypeRank(b) || a.localeCompare(b);
}

type CatalogTab = "levels" | "types" | "skills";
type CatalogKind = "level" | "type" | "skill";

// What the add/edit dialog is showing: a kind, plus the row when editing.
type DialogState =
  | { kind: "level"; item?: ExpertiseLevel }
  | { kind: "type"; item?: SkillType }
  | { kind: "skill"; item?: SkillRow };

const ADD_LABEL: Record<CatalogTab, string> = {
  levels: "Add level",
  types: "Add type",
  skills: "Add skill",
};

const KIND_FOR_TAB: Record<CatalogTab, CatalogKind> = {
  levels: "level",
  types: "type",
  skills: "skill",
};

const API_PATH: Record<CatalogKind, string> = {
  level: "expertise-levels",
  type: "skill-types",
  skill: "skills",
};

const NOUN: Record<CatalogKind, string> = {
  level: "expertise level",
  type: "skill type",
  skill: "skill",
};

// The catalog is fetched whole because the review's pickers need every row;
// usePaged (shared.tsx) only splits what is shown.

// The skills catalog behind the review's Skills section: expertise levels, skill
// types and skills, one tab each.
export default function SkillsCatalogPanel({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [types, setTypes] = useState<SkillType[]>([]);
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [levels, setLevels] = useState<ExpertiseLevel[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState<CatalogTab>("levels");
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [skillTypeFilter, setSkillTypeFilter] = useState("ALL");

  const load = useCallback(async () => {
    try {
      const [t, s, l, d] = await Promise.all([
        api.get<SkillType[]>("/skill-types?includeInactive=true"),
        api.get<SkillRow[]>("/skills?includeInactive=true"),
        api.get<ExpertiseLevel[]>("/expertise-levels?includeInactive=true"),
        api.get<Department[]>("/admin/departments"),
      ]);
      setTypes(t.data);
      setSkills(s.data);
      setLevels(l.data);
      setDepartments(d.data);
    } catch (e) {
      logError("SkillsCatalogPanel.load", e);
      toast.show(errMsg(e) ?? "Could not load the skills catalog", "error");
    } finally {
      setLoaded(true);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const skillCountByType = useMemo(() => {
    const m = new Map<string, number>();
    for (const sk of skills) m.set(sk.skillTypeId, (m.get(sk.skillTypeId) ?? 0) + 1);
    return m;
  }, [skills]);

  // Technical, then Soft, then Functional, then any other type — used for the
  // Skill Types table, the type filter/picker dropdowns, and to group the
  // Skills table below by the same order.
  const sortedTypes = useMemo(
    () => [...types].sort((a, b) => compareSkillTypeName(a.name, b.name)),
    [types],
  );

  const visibleSkills = useMemo(() => {
    const q = skillSearch.trim().toLowerCase();
    return skills
      .filter(
        (sk) =>
          (skillTypeFilter === "ALL" || sk.skillTypeId === skillTypeFilter) &&
          (!q ||
            sk.name.toLowerCase().includes(q) ||
            sk.skillType.name.toLowerCase().includes(q) ||
            skillDisplayName(sk).toLowerCase().includes(q)),
      )
      .sort(
        (a, b) =>
          compareSkillTypeName(a.skillType.name, b.skillType.name) || a.name.localeCompare(b.name),
      );
  }, [skills, skillSearch, skillTypeFilter]);

  const levelPages = usePaged(levels, "levels");
  const typePages = usePaged(sortedTypes, "types");
  const skillPages = usePaged(visibleSkills, `${skillSearch.trim()}|${skillTypeFilter}`);

  // Deactivate hides a row from every picker but keeps it, so employees who
  // already hold it keep their records intact.
  async function setActive(kind: CatalogKind, id: string, isActive: boolean) {
    try {
      await api.patch(`/${API_PATH[kind]}/${id}`, { isActive });
      toast.show(isActive ? "Activated" : "Deactivated", "success");
      void load();
    } catch (e) {
      logError("SkillsCatalogPanel.setActive", e);
      toast.show(errMsg(e) ?? "Could not update", "error");
    }
  }

  // The server refuses to delete anything still in use and says why, so its
  // message is shown as-is rather than guessed at here.
  async function remove(kind: CatalogKind, id: string, name: string) {
    const ok = await confirm({
      title: `Delete this ${NOUN[kind]}?`,
      description: `"${name}" will be removed permanently. If it is still in use it cannot be deleted — deactivate it instead.`,
      actionText: "Delete",
      tone: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/${API_PATH[kind]}/${id}`);
      toast.show(`${name} deleted`, "success");
      void load();
    } catch (e) {
      logError("SkillsCatalogPanel.remove", e);
      toast.show(errMsg(e) ?? "Could not delete", "error");
    }
  }

  // Only the very first load shows a placeholder; later reloads keep the table
  // (and the chosen tab) on screen while fresh data arrives.
  if (!loaded) return <p className="p-8 text-surface-500">Loading…</p>;

  const addDisabled = tab === "skills" && types.length === 0;

  // No card header: the page heading above already names this screen, so the
  // Add button sits at the end of the tab bar instead.
  return (
    <div className="rounded-lg border border-surface-200 bg-surface-0 p-5">
      <Tabs value={tab} onValueChange={(v) => setTab(v as CatalogTab)}>
        <div className="flex items-end justify-between gap-3 border-b border-surface-200">
          <TabsList className="border-b-0">
            <TabsTrigger value="levels">
              Expertise Levels <CountPill n={levels.length} />
            </TabsTrigger>
            <TabsTrigger value="types">
              Skill Types <CountPill n={types.length} />
            </TabsTrigger>
            <TabsTrigger value="skills">
              Skills <CountPill n={skills.length} />
            </TabsTrigger>
          </TabsList>
          {canEdit && (
            <div className="mb-2 flex items-center gap-2">
              {tab === "skills" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={addDisabled}
                  title={addDisabled ? "Add a skill type first" : "Import skills from a spreadsheet"}
                  onClick={() => setImportOpen(true)}
                >
                  <Upload size={14} /> Import
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={addDisabled}
                title={addDisabled ? "Add a skill type first" : undefined}
                onClick={() => setDialog({ kind: KIND_FOR_TAB[tab] } as DialogState)}
              >
                <Plus size={14} /> {ADD_LABEL[tab]}
              </Button>
            </div>
          )}
        </div>

        {/* ── Expertise levels ── */}
        <TabsContent value="levels">
          {levels.length === 0 ? (
            <EmptyState message="No expertise levels yet. Skills cannot be assigned until at least one exists." />
          ) : (
            <>
              <CatalogTable>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 pl-3">#</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Weight</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="pr-3 text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {levelPages.pageItems.map((l, i) => (
                    <TableRow key={l.id} className="hover:bg-surface-50">
                      <TableCell className="pl-3 text-surface-400 tabular-nums">{levelPages.offset + i + 1}</TableCell>
                      <TableCell className="font-medium whitespace-nowrap">{l.name}</TableCell>
                      <TableCell className="tabular-nums">{l.weightPercent}%</TableCell>
                      <TableCell className="text-surface-500 tabular-nums">{l.seq}</TableCell>
                      <TableCell>
                        <ActiveBadge active={l.isActive} />
                      </TableCell>
                      {canEdit && (
                        <TableCell className="pr-3 text-center">
                          <RowActions
                            name={l.name}
                            active={l.isActive}
                            onEdit={() => setDialog({ kind: "level", item: l })}
                            onToggle={() => void setActive("level", l.id, !l.isActive)}
                            onDelete={() => void remove("level", l.id, l.name)}
                            inUseReason={blockedBy(l._count?.employeeSkills ?? 0, "employee skill")}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </CatalogTable>
              <Pagination meta={levelPages.meta} onPageChange={levelPages.setPage} />
            </>
          )}
        </TabsContent>

        {/* ── Skill types ── */}
        <TabsContent value="types">
          {types.length === 0 ? (
            <EmptyState message="No skill types yet." />
          ) : (
            <>
              <CatalogTable>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 pl-3">#</TableHead>
                    <TableHead>Skill type</TableHead>
                    <TableHead>Prefix</TableHead>
                    <TableHead>Skills</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="pr-3 text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {typePages.pageItems.map((t, i) => (
                    <TableRow key={t.id} className="align-top hover:bg-surface-50">
                      <TableCell className="pl-3 text-surface-400 tabular-nums">{typePages.offset + i + 1}</TableCell>
                      <TableCell className="font-medium whitespace-nowrap">{t.name}</TableCell>
                      <TableCell>
                        {t.prefix ? (
                          <code className="rounded bg-surface-100 px-1.5 py-0.5 text-[12px]">{t.prefix}</code>
                        ) : (
                          <span className="text-surface-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">{skillCountByType.get(t.id) ?? 0}</TableCell>
                      <TableCell>
                        <ActiveBadge active={t.isActive} />
                      </TableCell>
                      {canEdit && (
                        <TableCell className="pr-3 text-center">
                          <RowActions
                            name={t.name}
                            active={t.isActive}
                            onEdit={() => setDialog({ kind: "type", item: t })}
                            onToggle={() => void setActive("type", t.id, !t.isActive)}
                            onDelete={() => void remove("type", t.id, t.name)}
                            inUseReason={blockedBy(skillCountByType.get(t.id) ?? 0, "skill")}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </CatalogTable>
              <Pagination meta={typePages.meta} onPageChange={typePages.setPage} />
            </>
          )}
        </TabsContent>

        {/* ── Skills ── */}
        <TabsContent value="skills">
          {skills.length === 0 ? (
            <EmptyState
              message={types.length === 0 ? "Add a skill type first, then add skills to it." : "No skills in the catalog yet."}
            />
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <SearchInput
                  placeholder="Search skills"
                  value={skillSearch}
                  onChange={(e) => setSkillSearch(e.target.value)}
                />
                <DropdownSelect
                  value={skillTypeFilter}
                  onChange={setSkillTypeFilter}
                  options={[
                    { value: "ALL", label: "All skill types" },
                    ...sortedTypes.map((t) => ({ value: t.id, label: t.name })),
                  ]}
                  placeholder="All skill types"
                  ariaLabel="Skill type"
                  clearable={false}
                  className="w-48 justify-between"
                  open={openDropdown === "skillTypeFilter"}
                  onToggle={() => setOpenDropdown((d) => (d === "skillTypeFilter" ? null : "skillTypeFilter"))}
                  onClose={() => setOpenDropdown((d) => (d === "skillTypeFilter" ? null : d))}
                />
                <span className="ml-auto text-[12px] text-surface-500">
                  {visibleSkills.length === skills.length
                    ? `${skills.length} skill${skills.length === 1 ? "" : "s"}`
                    : `${visibleSkills.length} of ${skills.length} skills`}
                </span>
              </div>

              <CatalogTable>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 pl-3">#</TableHead>
                    <TableHead>Skill</TableHead>
                    <TableHead>Skill type</TableHead>
                    <TableHead>Applies to</TableHead>
                    <TableHead>Default</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="pr-3 text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleSkills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 7 : 6} className="py-8 text-center text-[13px] text-surface-400">
                        No skills match your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    skillPages.pageItems.map((sk, i) => (
                      <TableRow key={sk.id} className="hover:bg-surface-50">
                        <TableCell className="pl-3 text-surface-400 tabular-nums">{skillPages.offset + i + 1}</TableCell>
                        <TableCell className="font-medium whitespace-nowrap">{sk.name}</TableCell>
                        <TableCell className="text-surface-500 whitespace-nowrap">{sk.skillType.name}</TableCell>
                        <TableCell>
                          <SkillScope skill={sk} departments={departments} />
                        </TableCell>
                        <TableCell>
                          {sk.isDefault ? <Badge tone="info">Default</Badge> : <span className="text-surface-400">—</span>}
                        </TableCell>
                        <TableCell>
                          <ActiveBadge active={sk.isActive} />
                        </TableCell>
                        {canEdit && (
                          <TableCell className="pr-3 text-center">
                            <RowActions
                              name={sk.name}
                              active={sk.isActive}
                              onEdit={() => setDialog({ kind: "skill", item: sk })}
                              onToggle={() => void setActive("skill", sk.id, !sk.isActive)}
                              onDelete={() => void remove("skill", sk.id, sk.name)}
                              inUseReason={blockedBy(sk._count?.employeeSkills ?? 0, "employee")}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </CatalogTable>
              {visibleSkills.length > 0 && (
                <Pagination meta={skillPages.meta} onPageChange={skillPages.setPage} />
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      <SkillImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={() => void load()} />

      <CatalogDialog
        state={dialog}
        types={sortedTypes}
        departments={departments}
        hasDefaultLevel={levels.some((l) => l.isDefault && l.isActive)}
        onClose={() => setDialog(null)}
        onSaved={load}
      />
    </div>
  );
}

// ─── Small shared pieces for the three tables ───────────────────────────────

interface Department {
  id: string;
  name: string;
}

// "Engineer", "Team lead" — the stored value is SCREAMING_SNAKE.
function roleLabel(role: string): string {
  const s = role.replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// A scope list means "everyone" when it is empty or names every option —
// picking all departments (or all roles) is the same as picking none.
function coversAll(selected: string[], all: string[]): boolean {
  if (selected.length === 0) return true;
  const chosen = new Set(selected);
  return all.length > 0 && all.every((v) => chosen.has(v));
}

// The Applies to cell: who a skill is offered to on reviews. Departments and
// roles are labelled lines, so a department "Engineering" and a role
// "Engineer" never read as the same thing twice. Each line says "Everyone"
// when it covers every department / role.
function SkillScope({ skill, departments }: { skill: SkillRow; departments: Department[] }) {
  const allDepts = coversAll(skill.departmentIds, departments.map((d) => d.id));
  const allRoles = coversAll(skill.roles, ASSIGNABLE_ROLES);
  if (allDepts && allRoles) {
    return <span className="text-[12px] text-surface-400">Everyone</span>;
  }
  const deptName = new Map(departments.map((d) => [d.id, d.name]));
  const depts = allDepts ? [] : skill.departmentIds.map((id) => deptName.get(id) ?? "Unknown department");
  return (
    <div className="grid max-w-xs gap-0.5 text-[12px]">
      <ScopeLine label="Departments" values={depts} />
      <ScopeLine label="Roles" values={allRoles ? [] : skill.roles.map(roleLabel)} />
    </div>
  );
}

// Up to this many names per line; the rest collapse into "+N more", with the
// full list in the tooltip, so a long scope never wraps the row.
const SCOPE_NAMED = 2;

function ScopeLine({ label, values }: { label: string; values: string[] }) {
  const shown = values.slice(0, SCOPE_NAMED).join(", ");
  const more = values.length - SCOPE_NAMED;
  return (
    <div className="truncate whitespace-nowrap" title={values.length > SCOPE_NAMED ? values.join(", ") : undefined}>
      <span className="text-surface-400">{label}: </span>
      {values.length === 0 ? (
        <span className="text-surface-400">Everyone</span>
      ) : (
        <>
          {shown}
          {more > 0 && <span className="text-surface-500">, +{more} more</span>}
        </>
      )}
    </div>
  );
}

// min-w-max stops the table shrinking below its content's natural width on a
// narrow screen, so the wrapper's overflow-x-auto scrolls horizontally instead
// of every column cramming and wrapping its text.
function CatalogTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-md border border-surface-200">
      <Table className="min-w-max">{children}</Table>
    </div>
  );
}

function CountPill({ n }: { n: number }) {
  return (
    <span className="ml-1.5 rounded-full bg-surface-100 px-1.5 py-0.5 text-[11px] font-semibold text-surface-500 tabular-nums">
      {n}
    </span>
  );
}

// The Delete tooltip while a row is in use, or undefined once it is free.
function blockedBy(n: number, noun: string): string | undefined {
  if (n === 0) return undefined;
  return `In use by ${n} ${noun}${n === 1 ? "" : "s"} — unassign first`;
}

function ActiveBadge({ active }: { active: boolean }) {
  return <Badge tone={active ? "success" : "error"}>{active ? "Active" : "Inactive"}</Badge>;
}

// Row actions, in the same shape the Expenses list uses for its rows: ghost
// icon Buttons at 14px, edit in grey and delete in red. Order is status toggle,
// then edit, then delete. Any row-specific extra goes in `children`, ahead of
// the three.
function RowActions({
  name,
  active,
  onEdit,
  onToggle,
  onDelete,
  inUseReason,
  children,
}: {
  name: string;
  active: boolean;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  /**
   * Set while the row is in use: Delete and Deactivate are both disabled and
   * this is their tooltip. Activating an inactive row stays possible.
   */
  inUseReason?: string | undefined;
  children?: React.ReactNode;
}) {
  const toggleBlocked = active && !!inUseReason;
  const toggleLabel = toggleBlocked
    ? `Cannot deactivate ${name}: ${inUseReason}`
    : active
      ? `Deactivate ${name}`
      : `Activate ${name}`;
  return (
    <div className="flex items-center justify-center gap-0.5">
      {children}
      {/* The span carries the tooltip: a disabled button shows none of its own. */}
      <span
        title={toggleBlocked ? inUseReason : active ? "Active — click to deactivate" : "Inactive — click to activate"}
        className={toggleBlocked ? "cursor-not-allowed" : undefined}
      >
        <Button
          onClick={onToggle}
          disabled={toggleBlocked}
          aria-label={toggleLabel}
          variant="ghost"
          size="icon"
          className={active ? "text-success" : "text-error"}
        >
          {active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
        </Button>
      </span>
      <EditIconButton name={name} onClick={onEdit} />
      <DeleteIconButton name={name} onClick={onDelete} blockedReason={inUseReason} />
    </div>
  );
}

// One dialog for adding and editing all three kinds. Editing pre-fills the
// form from the row and PATCHes it; adding POSTs a new one.
function CatalogDialog({
  state,
  types,
  departments,
  hasDefaultLevel,
  onClose,
  onSaved,
}: {
  state: DialogState | null;
  types: SkillType[];
  departments: Department[];
  hasDefaultLevel: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [openFormDropdown, setOpenFormDropdown] = useState<string | null>(null);
  const [skillTypeId, setSkillTypeId] = useState("");
  const [weightPercent, setWeightPercent] = useState("");
  const [seq, setSeq] = useState("");
  const [departmentIds, setDepartmentIds] = useState<Set<string>>(new Set());
  const [roles, setRoles] = useState<Set<string>>(new Set());
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const kind = state?.kind ?? null;
  const editing = !!state?.item;

  useEffect(() => {
    setError("");
    if (!state) return;
    if (state.kind === "level") {
      setName(state.item?.name ?? "");
      setWeightPercent(state.item ? String(state.item.weightPercent) : "");
      setSeq(state.item ? String(state.item.seq) : "");
      setIsDefault(state.item?.isDefault ?? false);
    } else if (state.kind === "type") {
      setName(state.item?.name ?? "");
      setPrefix(state.item?.prefix ?? "");
      setIsDefault(false);
    } else {
      setName(state.item?.name ?? "");
      setSkillTypeId(state.item?.skillTypeId ?? "");
      setDepartmentIds(new Set(state.item?.departmentIds ?? []));
      setRoles(new Set(state.item?.roles ?? []));
      setIsDefault(state.item?.isDefault ?? false);
    }
  }, [state]);

  async function save() {
    if (!state) return;
    setSaving(true);
    setError("");
    try {
      const path = `/${API_PATH[state.kind]}`;
      const payload =
        state.kind === "level"
          ? { name: name.trim(), weightPercent: Number(weightPercent), isDefault, ...(seq !== "" ? { seq: Number(seq) } : {}) }
          : state.kind === "type"
            ? { name: name.trim(), prefix: prefix || null }
            : {
                name: name.trim(),
                skillTypeId,
                isDefault,
                // Every option ticked is stored as "everyone" (empty), so the
                // skill also covers departments / roles added later.
                departmentIds: coversAll(Array.from(departmentIds), departments.map((d) => d.id))
                  ? []
                  : Array.from(departmentIds),
                roles: coversAll(Array.from(roles), ASSIGNABLE_ROLES) ? [] : Array.from(roles),
              };

      if (state.item) await api.patch(`${path}/${state.item.id}`, payload);
      else await api.post(path, payload);

      toast.show(editing ? "Changes saved" : "Added", "success");
      onSaved();
      onClose();
    } catch (e) {
      logError("SkillsCatalogPanel.save", e);
      setError(errMsg(e) ?? "Could not save");
    } finally {
      setSaving(false);
    }
  }

  const weightValid = weightPercent !== "" && Number(weightPercent) >= 0 && Number(weightPercent) <= 100;
  const ready =
    kind === "type"
      ? name.trim().length > 0
      : kind === "skill"
        ? name.trim().length > 0 && !!skillTypeId
        : name.trim().length > 0 && weightValid;

  const title = kind ? `${editing ? "Edit" : "Add"} ${NOUN[kind]}` : "";

  return (
    <Dialog open={state != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="first-letter:uppercase">{title}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          {kind === "type" && (
            <Field label="Display prefix (optional)" hint='For example "SAP" so skills read "SAP-FICO".'>
              <Input value={prefix} onChange={(e) => setPrefix(e.target.value)} />
            </Field>
          )}
          {kind === "skill" && (
            <Field label="Skill type">
              <DropdownSelect
                value={skillTypeId}
                onChange={setSkillTypeId}
                options={types.map((t) => ({ value: t.id, label: t.name }))}
                placeholder="Select a type"
                ariaLabel="Skill type"
                clearable={false}
                className="w-full justify-between"
                open={openFormDropdown === "skillType"}
                onToggle={() => setOpenFormDropdown((d) => (d === "skillType" ? null : "skillType"))}
                onClose={() => setOpenFormDropdown((d) => (d === "skillType" ? null : d))}
              />
            </Field>
          )}
          {kind === "skill" && (
            <>
              <Field label="Departments" hint="">
                <MultiSelect
                  options={departments.map((d) => ({ value: d.id, label: d.name }))}
                  selected={departmentIds}
                  onSelectedChange={setDepartmentIds}
                  placeholder="All departments"
                  searchPlaceholder="Search departments"
                  emptyText="No department matches."
                />
              </Field>
              <Field label="Roles" hint="">
                <MultiSelect
                  options={ASSIGNABLE_ROLES.map((r) => ({ value: r, label: roleLabel(r) }))}
                  selected={roles}
                  onSelectedChange={setRoles}
                  placeholder="All roles"
                  searchPlaceholder="Search roles"
                  emptyText="No role matches."
                />
              </Field>
              <label className="flex items-start gap-2.5 rounded-md border border-surface-200 p-3">
                <Checkbox
                  checked={isDefault}
                  onCheckedChange={(v) => setIsDefault(v === true)}
                  disabled={!hasDefaultLevel && !isDefault}
                  className="mt-0.5"
                />
                <span className="text-[13px]">
                  <span className="font-medium">Default skill</span>
                  <span className="block text-surface-500">
                    Automatically assigned to everyone in this department and role, at the default expertise level.
                    {!hasDefaultLevel && " Set a default expertise level first (Expertise Levels tab)."}
                  </span>
                </span>
              </label>
            </>
          )}
          {kind === "level" && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Weight (%)">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={weightPercent}
                    onChange={(e) => setWeightPercent(e.target.value)}
                  />
                </Field>
                {/* New levels are ordered automatically after the last one; the
                    order can be changed afterwards when editing. */}
                {state?.item && (
                  <Field label="Order" hint="Lower numbers list first.">
                    <Input type="number" min={0} value={seq} onChange={(e) => setSeq(e.target.value)} />
                  </Field>
                )}
              </div>
              <label className="flex items-start gap-2.5 rounded-md border border-surface-200 p-3">
                <Checkbox
                  checked={isDefault}
                  onCheckedChange={(v) => setIsDefault(v === true)}
                  className="mt-0.5"
                />
                <span className="text-[13px]">
                  <span className="font-medium">Default level</span>
                  <span className="block text-surface-500">
                    Stamped on skills that are auto-assigned to a department or role. Only one level can be the
                    default; choosing this clears any other.
                  </span>
                </span>
              </label>
            </>
          )}
          {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || !ready} onClick={() => void save()}>
            {saving ? "Saving…" : editing ? "Save changes" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
